// src/components/LandingPage/Header.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { FiLogOut, FiMenu, FiX } from 'react-icons/fi';
import { logout } from '../../../services/utils/processors';

const HeaderWrapper = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 4rem;
  background: ${({ theme }) => `${theme.cardBackground}f0`};
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${({ theme }) => `${theme.text}10`};
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
  }
`;

const Logo = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #58a6ff 0%, #79c0ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.05em;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;

  &:hover {
    transform: scale(1.02);
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: 1.8rem;

  a {
    position: relative;
    font-weight: 500;
    font-size: 0.95rem;
    color: ${({ theme }) => theme.text}dd;
    text-decoration: none;
    transition: all 0.3s ease;
    padding: 0.5rem 0.25rem;

    &:hover {
      color: #58a6ff;
    }

    &::after {
      content: '';
      position: absolute;
      width: 0%;
      height: 2px;
      left: 0;
      bottom: -6px;
      background: linear-gradient(90deg, #58a6ff, #79c0ff);
      transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border-radius: 2px;
    }

    &:hover::after {
      width: 100%;
    }
  }

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: ${({ open }) => (open ? '0' : '-100%')};
    height: 100vh;
    width: 250px;
    background: var(--bg-card);
    flex-direction: column;
    justify-content: center;
    padding: 2rem;
    gap: 2rem;
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
    transition: right 0.3s ease;
    z-index: 1100;
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-main);
  font-size: 1.8rem;

  @media (max-width: 768px) {
    display: block;
  }
`;

const UserIndicator = styled.button`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 1.2rem;
  border: none;
  cursor: pointer;
  font-family: inherit;
  background: linear-gradient(135deg, #58a6ff 0%, #79c0ff 100%);
  color: white;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(88, 166, 255, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(88, 166, 255, 0.4);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }
`;

const Header = ({ isAuthenticated = false }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => setMenuOpen((prev) => !prev);

  return (
    <HeaderWrapper>
      <Logo>Portify</Logo>

      <NavLinks open={menuOpen} onClick={() => setMenuOpen(false)}>
        <a href="#features">Features</a>
        <a href="#templates">Templates</a>
        <a href="#contact">Contact</a>
      </NavLinks>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        {isAuthenticated && (
          <UserIndicator type="button" onClick={logout} aria-label="Log out">
            <FiLogOut aria-hidden="true" />
            Log out
          </UserIndicator>
        )}

        <MenuButton
          onClick={toggleMenu}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        >
          {menuOpen ? <FiX /> : <FiMenu />}
        </MenuButton>
      </div>
    </HeaderWrapper>
  );
};

export default Header;
